import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import { getRefGenome } from "../../../../state/selectors/general.selectors";
import PositionLink from "./PositionLink";
import Table from "../../../../components/atoms/SimpleTable";
import {
  sliceEventHeader,
  skippedExonIdHeader,
  crypticPositionHeader,
  missplicingInFrameHeader,
  samplesGtexHeader,
  maxReadCountHeader,
  spliceJunctionHeader,
  nSamplesHeaderHg19,
  nSamplesHeaderHg38,
} from "./SpliceVaultHeaders";
import SpliceAiEvent from "../../../../models/SpliceAiEvent";

function SpliceVaultTable({ type, exonNo, transcript, events }) {
  const refGenome = useSelector(getRefGenome);
  const isHg19 = refGenome === "hg19";

  const columns = useMemo(
    () => [
      {
        id: "slice_event",
        HeaderRenderer: () => sliceEventHeader,
        CellRenderer: ({ item }) => item.eventClass ?? "-",
      },

      {
        id: "skipped_exon_id",
        HeaderRenderer: () => skippedExonIdHeader,
        CellRenderer: ({ item }) => item.skippedExonsId ?? "-",
      },
      {
        id: "cryptic_position",
        HeaderRenderer: () => crypticPositionHeader,
        CellRenderer: ({ item }) => item.crypticDistance ?? "-",
      },
      {
        id: "missplicing_in_frame",
        HeaderRenderer: () => missplicingInFrameHeader,
        CellRenderer: ({ item }) => item?.missplicingInFrame ?? "-",
      },
      {
        id: "samples_gtex",
        HeaderRenderer: () => samplesGtexHeader,
        CellRenderer: ({ item }) => item?.gtexSampleCount ?? "-",
      },
      {
        id: "max_read_count",
        HeaderRenderer: () => maxReadCountHeader,
        CellRenderer: ({ item }) => item?.gtexMaxUniqMapReads ?? "-",
      },
      isHg19
        ? {
            id: "n_samples",
            HeaderRenderer: () => nSamplesHeaderHg19,
            CellRenderer: ({ item }) => item.sampleCountHg19 ?? "-",
          }
        : {
            id: "n_samples",
            HeaderRenderer: () => nSamplesHeaderHg38,
            CellRenderer: ({ item }) => item.sampleCountHg38 ?? "-",
          },
      {
        id: "splice_junction",
        HeaderRenderer: () => spliceJunctionHeader,
        transformCellProps: (item) => ({
          chr: item.chr,
          x0: item.x0,
          x1: item.x1,
        }),
        CellRenderer: PositionLink,
      },
    ],
    [isHg19]
  );

  return (
    <>
      <div className="tw-mb-2 tw-text-blueGray-500">
        Reporting the top 4 most common unannotated mis-splicing events for{" "}
        <b>{transcript}</b> {type} {exonNo}, using SpliceVault default settings
        (max 2 exons skipped, cryptics within +/-600 nt,{" "}
        <a
          href="https://www.nature.com/articles/s41588-022-01293-8"
          target="_blank"
          rel="noopener noreferrer"
        >
          Dawes et al. 2023
        </a>
        )
      </div>
      <div className="tw-text-sm tw-w-full tw-overflow-auto">
        <Table items={events} columns={columns} />
      </div>
    </>
  );
}

SpliceVaultTable.propTypes = {
  type: PropTypes.string.isRequired,
  exonNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  transcript: PropTypes.string.isRequired,
  events: PropTypes.arrayOf(PropTypes.instanceOf(SpliceAiEvent)).isRequired,
};

export default withErrorGuard(SpliceVaultTable);
